<template>
    <div>
        <div
            v-if="visible"
            class="crm-order-state-control"
            :class="{ 'crm-order-state-control--light': showEditorLightStyle }"
        >
            <div v-if="comebackUrl">
                <a
                    :href="comebackUrl"
                    :style="{ color: showEditorLightStyle ? 'rgb(30,30,30)' : '#fff' }"
                >
                    &lt; Return
                </a>
            </div>
            <div
                v-if="showEditorLightStyle && transitions.length"
                style="padding-left: 10px; color: #1e1e1e"
            >
                To receive the final file, please click the "{{ transitions[0].action_text.eng }}"
                button
            </div>

            <div v-if="showEditorLightStyle" style="margin: 0 auto">
                <EditorSwitch />
            </div>

            <div v-if="transitions.length" class="crm-order-state-control__transitions">
                <div
                    v-for="transition in transitions"
                    :key="transition.destination_state"
                    class="crm-order-state-control__inner"
                >
                    <button
                        class="button button--fullwidth button--outlined crm-order-state-control__button"
                        type="button"
                        :class="{ 'is-disabled': !transition.is_accept }"
                        :disabled="!transition.is_accept"
                        @click="activeTransition = transition"
                    >
                        {{ transition.action_text[$i18n.locale] }}
                    </button>
                </div>
            </div>
        </div>
        <portal v-if="activeTransition" to="modals">
            <ConfirmModal
                :title="activeTransition.action_text[$i18n.locale]"
                :ok-label="activeTransition.action_text[$i18n.locale]"
                :ok-disabled="!activeTransition.is_accept"
                :cancel-label="$t('editor.crmOrderState.cancel')"
                buttons-reverse
                @confirmed="changeState(activeTransition)"
                @canceled="activeTransition = null"
            >
                <template #content>
                    <Spinner v-if="loading" />
                    <div class="crm-order-state-control__modal">
                        <span v-text="activeTransition.description[$i18n.locale]" />
                    </div>
                </template>
            </ConfirmModal>
        </portal>
        <portal v-if="showCompleteModal" to="modals">
            <Modal name="name" :title="$t('editor.crmOrderState.orderCompletedMessage')" unclosable>
                <template #content>
                    <div style="text-align: center">
                        <!-- {{ $t('editor.crmOrderState.orderCompleted') }} -->
                        <button
                            class="button button--medium"
                            type="button"
                            style="display: block; margin: 20px auto 0"
                            @click="signalCloseEditor"
                        >
                            Ok
                        </button>
                    </div>
                </template>
            </Modal>
        </portal>
    </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';

import { ConfirmModal, Modal } from '@speechki-frontend/shared/ui/modals';
import EditorSwitch from '../EditorSwitch/EditorSwitch.vue';

import { fetchEmptyParts } from '../../api/books';
import { fetchAvailableTransitions, changeState, fetchOrderShort } from '../../api/crm';
import {
    getAllowedEmbedParentOrigins,
    getEmbedPostMessageTargetOrigins,
    isTrustedEmbedParentMessage,
} from '../../utils/embedParentOrigin';

// @todo move parseJwt to shared
import { parseJwt } from '@speechki-frontend/core/auth/utils';

export default {
    name: 'CRMOrderStateControl',
    components: {
        ConfirmModal,
        Modal,
        EditorSwitch,
    },

    inject: ['registerWsHandler', 'removeWsHandler', 'customerSettings'],

    data() {
        return {
            showModal: false,
            showCompleteModal: false,
            transitions: [],
            order: {},
            activeTransition: null,
            loading: false,

            pingInterval: undefined,
            availableTransitionsRequestId: null,
            lastAvailableTransitionsPayload: null,
            availableTransitionsAcknowledged: false,
            allowedEmbedParentOrigins: getAllowedEmbedParentOrigins(),
        };
    },

    computed: {
        ...mapState({
            orderId: (state) => state.book.book.order_id,
        }),
        ...mapState('guestAccess', {
            comebackUrl: (state) => state.comebackUrl,
        }),
        ...mapGetters('guestAccess', ['guestAccessActionPermission']),
        bookId() {
            return this.$route.params.id;
        },
        visible() {
            if (this.isEmbedded && this.availableTransitionsAcknowledged) return false;

            return !!this.comebackUrl || !!this.transitions.length;
        },
        isEmbedded() {
            return window.parent && window.parent !== window;
        },
        showEditorLightStyle() {
            return this.customerSettings.settings.name === 'plugin';
        },
    },

    watch: {
        visible: {
            handler() {
                this.$emit('visible', this.visible);
            },
            immediate: true,
        },
    },

    async created() {
        this.loading = true;
        const token = sessionStorage.getItem('token');

        if (!token) return;

        const { order_id } = parseJwt(token);

        await Promise.all([this.fetchAvailableTransitions(), this.fetchOrder(order_id)]);

        if (this.order.state && this.order.state.slug === 'order_completed') {
            this.showCompleteModal = true;
        }

        this.loading = false;
    },

    mounted() {
        this.registerWsHandler('order_complete', this.onOrderCompleteMessage);
        this.registerWsHandler('order_change_state', this.onOrderChangeState);
        this.registerWsHandler('updated_conditions', this.fetchAvailableTransitions);
        window.addEventListener('message', this.onAvailableTransitionsAcknowledgement);
        this.setupPing();
    },

    beforeUnmount() {
        this.removeWsHandler('order_complete', this.onOrderCompleteMessage);
        this.removeWsHandler('order_change_state', this.onOrderChangeState);
        this.removeWsHandler('updated_conditions', this.fetchAvailableTransitions);
        window.removeEventListener('message', this.onAvailableTransitionsAcknowledgement);
        this.destroyPing();
    },

    methods: {
        ...mapActions(['pushToast']),
        //
        async fetchAvailableTransitions() {
            try {
                this.transitions = await fetchAvailableTransitions();
                this.notifyParentAboutAvailableTransitions();
            } catch (e) {
                console.log(e);
            }
        },
        notifyParentAboutAvailableTransitions() {
            if (!this.isEmbedded) return;

            const payload = JSON.stringify(this.transitions);

            if (payload === this.lastAvailableTransitionsPayload) return;

            this.lastAvailableTransitionsPayload = payload;
            this.availableTransitionsAcknowledged = false;
            this.availableTransitionsRequestId = `${Date.now()}-${Math.random()}`;
            const transitions = JSON.parse(payload);

            const message = {
                event: 'speechki-available-transitions',
                data: {
                    requestId: this.availableTransitionsRequestId,
                    transitions,
                },
            };

            getEmbedPostMessageTargetOrigins(this.allowedEmbedParentOrigins).forEach(
                (targetOrigin) => {
                    window.parent.postMessage(message, targetOrigin);
                }
            );
        },
        onAvailableTransitionsAcknowledgement(message) {
            const { data } = message;

            if (!isTrustedEmbedParentMessage(message, this.allowedEmbedParentOrigins) || !data) {
                return;
            }

            if (data.event === 'speechki-available-transitions-ack') {
                if (
                    !data.data ||
                    data.data.requestId !== this.availableTransitionsRequestId ||
                    data.data.handled !== true
                ) {
                    return;
                }

                this.availableTransitionsAcknowledged = true;
                return;
            }

            if (data.event === 'speechki-request-order-transition' && data.data) {
                const transition = this.transitions.find(
                    ({ destination_state, is_accept }) =>
                        destination_state === data.data.destinationState && is_accept === true
                );

                if (transition) this.activeTransition = transition;
            }
        },
        async fetchOrder(orderId) {
            try {
                this.order = await fetchOrderShort(orderId);
            } catch (e) {
                console.log(e);
            }
        },
        async changeState(transition) {
            this.loading = true;

            try {
                const { destination_state: state } = transition;

                if (this.guestAccessActionPermission('book_part', 'update')) {
                    const emptyParts = await fetchEmptyParts(this.bookId);
                    if (emptyParts.length) {
                        this.$emit('showEmptyParts', emptyParts);
                    } else {
                        this.orderChangeState(state);
                    }
                } else {
                    this.orderChangeState(state);
                }
            } catch (e) {
                this.pushToast({
                    type: 'error',
                    title: this.$t('editor.crmOrderState.error'),
                    text: this.$t('editor.crmOrderState.errorText'),
                });
            }
        },
        async orderChangeState(state) {
            await changeState(this.orderId, state);
            this.$emit('orderChangeState', state);

            this.pushToast({
                type: 'success',
                title: this.$t('editor.crmOrderState.success'),
                text: this.$t('editor.crmOrderState.successText'),
            });
        },
        onOrderCompleteMessage({ code, params }) {
            if (code !== 200 || !params.order || params.order.id !== this.order.id) return;

            this.showModal = false;
            this.showCompleteModal = true;
            this.signalCloseEditor();
        },

        async onOrderChangeState({ code, params, errors }) {
            const {
                order: { id = null },
            } = params;

            if (id !== this.order.id) return;
            if (code !== 200) {
                console.log(errors);
                return;
            }

            this.signalCloseEditor();
            this.activeTransition = null;
            await this.fetchAvailableTransitions();
            this.loading = false;
        },

        signalCloseEditor() {
            const source = {
                slug: this.order.state.slug,
                label: this.order.state.label.eng,
            };

            let destination = null;

            if (this.activeTransition) {
                destination = {
                    action_text: this.activeTransition.action_text.eng,
                    slug: this.activeTransition.destination_state_obj.slug,
                    label: this.activeTransition.destination_state_obj.label.eng,
                };
            }

            const data = {
                source_state: source,
                destination_state: destination,
            };

            this.showCompleteModal = true;

            // Public event for any embedding site (e.g. external integrations via
            // guest links), so it intentionally stays on '*' regardless of
            // VUE_APP_EMBED_PARENT_ORIGINS; it carries only order state names.
            if (window.parent && window.parent !== window) {
                window.parent.postMessage(
                    {
                        event: 'speechki-close-editor',
                        data,
                    },
                    '*'
                );
            }
        },

        setupPing() {
            if (window.parent && window.parent !== window) {
                // Public keep-alive for any embedding site; stays on '*' (no payload).
                this.pingInterval = setInterval(() => {
                    window.parent.postMessage(
                        {
                            event: 'speechki-ping',
                            data: null,
                        },
                        '*'
                    );
                }, 1800000);
            }
        },

        destroyPing() {
            if (this.pingInterval) {
                clearInterval(this.pingInterval);
                this.pingInterval = null;
            }
        },
    },
};
</script>

<style lang="scss" src="./CRMOrderStateControl.scss"></style>
